import { Button } from "@/components/Button/Button";
import { ButtonLink } from "@/components/Button/ButtonLink";
import { DocumentTitle } from "@/components/DocumentTitle/DocumentTitle";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { FormTable } from "@/components/FormTable/FormTable";
import { Input } from "@/components/Input/Input";
import { Select } from "@/components/Select/Select";
import { useApi } from "@/context/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { objectKeys } from "@terralux/common/objectKeys";
import { sentenceCase } from "@terralux/common/string";
import clsx from "clsx";
import type { InferRequestType, InferResponseType } from "hono";
import { useId } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import css from "./index.module.scss";

export const Route = createFileRoute("/_authenticated-user/superuser/rapporter/")({
  head: () => ({
    meta: [{ title: "Superuser statistikk | Alice & Bob" }],
  }),
  component: Page,
});

const months = {
  1: "Januar",
  2: "Februar",
  3: "Mars",
  4: "April",
  5: "Mai",
  6: "Juni",
  7: "Juli",
  8: "August",
  9: "September",
  10: "Oktober",
  11: "November",
  12: "Desember",
};

const formSchema = z.object({
  year: z
    .number({ required_error: "Påkrevd", invalid_type_error: "Påkrevd" })
    .min(2000, { message: "Ugyldig årstall" })
    .max(9999, { message: "Ugyldig årstall" }),
  month: z.number({ required_error: "Påkrevd" }).min(1).max(12),
});

function Page() {
  const { client } = useApi();
  const idPrefix = useId();

  const $get = client.superuser.reports.invoicing.$get;
  const mutation = useMutation<InferResponseType<typeof $get>, Error, InferRequestType<typeof $get>["query"]>({
    mutationFn: async (query) => {
      const res = await $get({ query });
      return await res.json();
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const form = useForm<z.input<typeof formSchema>>({
    reValidateMode: "onChange",
    defaultValues: {
      year: undefined,
      month: undefined,
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit: SubmitHandler<z.input<typeof formSchema>> = async (data) => {
    mutation.mutate({
      year: data.year.toString(),
      month: data.month.toString(),
    });
  };

  return (
    <>
      <DocumentTitle>Rapporter</DocumentTitle>

      <form className={css.form} onSubmit={form.handleSubmit(onSubmit)}>
        <div className={css.formFields}>
          <div className={css.formField}>
            <label className={css.formLabel} htmlFor={`${idPrefix}-year`}>
              År
            </label>
            <Input
              id={`${idPrefix}-year`}
              {...form.register("year", { valueAsNumber: true })}
              error={form.formState.errors.year}
            />
          </div>

          <div className={css.formField}>
            <label className={css.formLabel} htmlFor={`${idPrefix}-month`}>
              Måned
            </label>
            <Controller
              control={form.control}
              name="month"
              render={({ field, fieldState }) => (
                <Select
                  ref={field.ref}
                  id={`${idPrefix}-month`}
                  value={field.value}
                  error={fieldState.error?.message}
                  onChange={(event) => {
                    if (event.target.value !== "") {
                      field.onChange(parseInt(event.target.value, 10));
                    }
                  }}
                >
                  <option value=""></option>
                  {objectKeys(months).map((month) => (
                    <option key={month} value={month}>
                      {months[month]}
                    </option>
                  ))}
                </Select>
              )}
            />
          </div>
        </div>

        <Button type="submit" active={form.formState.isSubmitting}>
          Submit
        </Button>
      </form>

      {mutation.data && (
        <div className={clsx(css.organisationList, "u-flow")} data-flow-space={60}>
          {mutation.data.map((organisation, index) => (
            <div key={index}>
              <h2 className={css.organisationTitle}>{organisation.orgName}</h2>

              {organisation.documents.length > 0 ? (
                <div className="u-flow" data-flow-space={30}>
                  <FormTable>
                    <table>
                      <thead>
                        <tr>
                          <th>Oppdragsnummer</th>
                          <th>Dokumenttype</th>
                          <th>Tinglysingsdato</th>
                          <th>Antall signaturer</th>
                        </tr>
                      </thead>

                      <tbody>
                        {organisation.documents.map((document, index) => {
                          return (
                            <tr key={index}>
                              <td>{document.oppdragsnummer}</td>
                              <td>{sentenceCase(document.documentType)}</td>
                              <td>
                                <FormattedDate value={document.timestamp} />
                              </td>
                              <td>{document.signatureCount}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </FormTable>

                  <ButtonLink variant="secondary" to={organisation.csvUrl} target="_blank">
                    Last ned (CSV)
                  </ButtonLink>
                </div>
              ) : (
                <p>Ingen dokumenter</p>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
